import styles from './contract-details.module.scss';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import Container from '../../container/container';
import { ContractStateContext } from '../contract-details-reducer/contract-details-reducer';
import { useImmer } from 'use-immer';
import { Col, Row } from 'reactstrap';
import { Box, Icon, Link, Tab, Tabs, Typography } from '@frontend-workspace/react-shared-lib';
import { contractGetNextInvoice, contractGetRecurringDiscount, createContractItemFromOrderItem, isNewLicense } from '../../helpers/helpers';
import { IContactType, IContractLicense, ContractOrderTypesEnum, IOrderItem, IContractStateType, OrderStateEnum } from '../../common-types/common-types';
import ContractLicenses from '../contract-licenses/contract-licenses';
import ContractHistory from '../contract-history/contract-history';
import ContractInformation from '../contract-information/contract-information';
import ContractDetailsSkeleton from './contract-details-skeleton';
import { t } from 'i18next';

/* eslint-disable-next-line */
export interface ContractDetailsProps {
  headerActions?: ReactNode,
  contract: IContractStateType,
  onEditLicense: any,
  isHandlingSale: boolean,
  companies: Array<IContactType>,
  onNewSale: () => void,
  onContractInfoChange: () => void,
  onDeleteOrder: (id: string) => void,
  onHideOrder: (id: string) => void,
  onOpenMoveInvoice: () => void,
  reasonsForChange: { name: string }[],
  onReasonChange: (id: string, reason: string) => void
}

function ContractDetailsContent(props: ContractDetailsProps) {
  const navigation = useNavigate();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const location = useLocation();
  const contract = props.contract;
  const redirectToAllContracts = () => navigation(`/customers/${contract.companyNumber}/all-contracts`);
  const [groups, updateGroups] = useImmer(contract.groups);
  if (!contract) return null;

  const navigateToTabOne = () => setSelectedTab(0);
  const navigateToTabTwo = () => setSelectedTab(1);
  const navigateToTabThree = () => setSelectedTab(2);

  const hasHiddenOrders = contract.existingOrders && contract.existingOrders.some(o => o.state === OrderStateEnum.Failed);

  const newLicenseFromItem = (item: IOrderItem): IContractLicense => {
    const contractItem = createContractItemFromOrderItem(item);
    return {
      licenseNumber: item.licenseNumber,
      unitCost: item.recurringPrice,
      itemType: item.itemType,
      items: [contractItem],
      nextInvoiceCost: 0,
      productName: item.productName,
      numberOfSeats: 0,
      modules: []
    }
  }

  useEffect(() => {
    updateGroups((draft: any) => {
      draft.forEach((g: any) => {
        const existingGroup = contract.groups.find((gr: any) => gr.id === g.id);
        const exitingLicenses = existingGroup ? existingGroup.licenses : [];
        const newLicensesItems = contract.orderItems.filter((i: any) => i.groupId === g.id && i.type === ContractOrderTypesEnum.AddProductsToContract);
        const newLicenses = newLicensesItems ? newLicensesItems.filter((i: any) => isNewLicense(i.licenseNumber) && !i.isModule && !i.isConnectedItem).map(newLicenseFromItem) : [];
        g.licenses = exitingLicenses.concat(newLicenses);
        return g;
      });
    })
  }, [contract]);

  return (
    <Container
      className="container-fluid pt-4 px-0"
      maxWidth={false}
      sx={{ paddingBottom: '50px' }}>
      <Row>
        <Col>
          <Box
            component='div'
            className='pb-5'>
            <Link onClick={redirectToAllContracts} className='d-flex mb-3' underline='hover'>
              <Icon className='me-1'>arrow_back</Icon>{t("standard.contracts")}
            </Link>
            <div className={props.headerActions ? 'mb-5 d-flex align-items-center flex-wrap gap-2' : 'mb-5'}>
              <Typography
                variant='h2'
                component='span'
                sx={{ flexGrow: 1 }}>
                {t("standard.contract")}
              </Typography>
              <Typography
                variant="body6"
                component="span"
                className='ms-2'>
                {t("standard.contractNo")} <Link href={`/admin/contract/${props.contract.contractNumber}`} underline='always'>
                  {contract.contractNumber}
                </Link>
              </Typography>
              {props.headerActions}
            </div>
            {!props.contract.isLoaded ?
              <ContractDetailsSkeleton /> : <>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '20px' }}>
                  <Tabs
                    value={selectedTab}
                    variant="scrollable"
                    scrollButtons="auto">
                    <Tab label={t("standard.licenses")} onClick={navigateToTabOne} />
                    <Tab label={t("standard.details")} onClick={navigateToTabTwo} />
                    {!contract.isNew &&
                      <Tab label={`${t("standard.orderOverview")} ${hasHiddenOrders ? `(${t("standard.hiddenChanges")})` : ""}`} onClick={navigateToTabThree} />
                    }
                  </Tabs>
                </Box>
                {selectedTab === 0 &&
                  <ContractLicenses
                    groups={groups}
                    sellableProducts={contract.sellableProducts}
                    orderItems={contract.orderItems}
                    onEditLicense={props.onEditLicense}
                    contractInfo={contract.contractInfo}
                    nextInvoiceCost={contractGetNextInvoice(contract)}
                    recurringDiscount={contractGetRecurringDiscount(contract)}
                    onNewSale={props.onNewSale}
                    isHandlingSale={props.isHandlingSale}
                    saleDate={contract.newSaleDate || new Date()}
                    onOpenMoveInvoice={props.onOpenMoveInvoice}
                  />
                }
                {selectedTab === 1 &&
                  contract.changeContractInfoOrder && <ContractInformation
                    contractInfo={contract.changeContractInfoOrder.contractInfo}
                    invoiceInfo={contract.changeContractInfoOrder.invoiceInfo}
                    companyName={`${contract.companyName} (${contract.companyNumber})`}
                    companyList={props.companies}
                    contractEmployeeList={contract.contractEmployeeList || []}
                    invoiceEmployeeList={contract.invoiceEmployeeList || []}
                    onContractInfoChange={props.onContractInfoChange}
                  />
                }
                {selectedTab === 2 &&
                  contract.existingOrders && <ContractHistory
                    orders={contract.existingOrders}
                    reasonsForChange={props.reasonsForChange}
                    onReasonChange={props.onReasonChange}
                    onDeleteOrder={props.onDeleteOrder}
                    onHideOrder={props.onHideOrder}
                  />
                }
              </>
            }
          </Box>
        </Col>
      </Row>
    </Container>
  );
}

export function ContractDetails(props: {
  headerActions?: ReactNode,
  onEditLicense: any,
  companies: Array<IContactType>
  onNewSaleClick: () => void,
  onContractInfoChange: () => void,
  onDeleteOrder: (id: string) => void,
  onHideOrder: (id: string) => void,
  onOpenMoveInvoice: () => void,
  isHandlingSale: boolean,
  reasonsForChange: { name: string }[],
  onReasonChange: (id: string, reason: string) => void
}) {
  const contract = useContext(ContractStateContext);
  return contract ? <ContractDetailsContent
    headerActions={props.headerActions}
    contract={contract}
    companies={props.companies}
    onEditLicense={props.onEditLicense}
    onNewSale={props.onNewSaleClick}
    onContractInfoChange={props.onContractInfoChange}
    onDeleteOrder={props.onDeleteOrder}
    onHideOrder={props.onHideOrder}
    isHandlingSale={props.isHandlingSale}
    reasonsForChange={props.reasonsForChange}
    onOpenMoveInvoice={props.onOpenMoveInvoice}
    onReasonChange={props.onReasonChange}
  /> : null;
}

export default ContractDetails;